import type { ArrivalStep } from '@/api/types/ProviderApi'
import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Image } from 'expo-image'
import { useEffect, useRef, useState } from 'react'
import {
	type LayoutChangeEvent,
	type NativeScrollEvent,
	type NativeSyntheticEvent,
	ScrollView,
	View,
} from 'react-native'

/** Tall enough to recognise a doorway by, short enough to leave its caption on screen with it. */
const IMAGE_HEIGHT = 190

/** The gutter between one step and the next, as the web guide sets it. */
const GAP = 16

/**
 * One step of the walk, as the studio photographed it — the photograph only.
 *
 * The words used to ride inside each page, which meant the peeking edge of the next one showed a
 * sliver of its caption beside the caption being read. They sit under the carousel now and follow
 * the index, so the block of text stays where the eye left it and only the pictures move.
 */
const Step = ({ step, width }: { step: ArrivalStep; width: number }) => (
	<Image
		source={{ uri: step.imageUrl }}
		style={{ width, height: IMAGE_HEIGHT, borderRadius: 20 }}
		contentFit="cover"
		transition={200}
		accessibilityLabel={step.title}
	/>
)

/**
 * Where you are in the walk, one mark per step.
 *
 * A studio can write two steps or ten, and the dots hold either: the active one stretches rather
 * than colouring in, so the row reads as a position even when nine of them are the same width.
 * Tappable, because a dot beside the one you are on is the shortest way back.
 */
export const ArrivalDots = ({
	count,
	at,
	onPick,
}: { count: number; at: number; onPick: (index: number) => void }) => (
	<HStack gap={6} className="items-center justify-center">
		{/* Mapped over the step numbers rather than over a blank array, so each dot is keyed by
		    the step it stands for. */}
		{Array.from({ length: count }, (_, index) => index).map((step) => (
			<Pressable
				key={`step-${step}`}
				onPress={() => onPick(step)}
				accessibilityRole="button"
				accessibilityLabel={`Step ${step + 1} of ${count}`}
				hitSlop={10}
				className="h-[7px] rounded-full"
				style={{
					width: step === at ? 18 : 7,
					backgroundColor: step === at ? colors.ink : colors.line,
				}}
			/>
		))}
	</HStack>
)

/**
 * How much of the next photograph shows past the edge of this one.
 *
 * The peek is the affordance: nothing says "there is more this way" as plainly as the edge of
 * the next thing, and it costs no chrome to say it. It replaced a counter, two arrow discs and a
 * progress bar — four controls above a photograph, all answering one question about a short list.
 */
const PAGE = 0.88

/**
 * `index` and `onIndex` are the caller's, so the dots can be drawn somewhere else.
 *
 * They ride the sheet's title row now — beside the name of the thing they are counting rather
 * than under the photographs they count through, which is where they were and where they cost a
 * line of their own. The position is one fact and it cannot live in two components, so the guide
 * reports it and draws nothing.
 */
export const ArrivalGuide = ({
	steps,
	index,
	onIndex,
}: { steps: ArrivalStep[]; index: number; onIndex: (index: number) => void }) => {
	const scroll = useRef<ScrollView>(null)
	const [width, setWidth] = useState(0)
	// The last position this carousel reported. A dot tapped outside it changes `index` to
	// something this did not say, and that difference is the instruction to scroll — without it
	// the guide would chase its own scrolling and fight the thumb.
	const reported = useRef(0)

	const page = Math.round(width * PAGE)
	const stride = page + GAP

	useEffect(() => {
		if (stride <= 0 || index === reported.current) return
		reported.current = index
		scroll.current?.scrollTo({ x: index * stride, animated: true })
	}, [index, stride])

	if (steps.length === 0) {
		return null
	}

	// Which step the scroll has settled on, rounded to the nearest rather than floored: a thumb
	// that stops two thirds of the way across has chosen the next one.
	const onSettled = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
		if (stride <= 0) return
		const at = Math.max(
			0,
			Math.min(steps.length - 1, Math.round(event.nativeEvent.contentOffset.x / stride))
		)
		reported.current = at
		onIndex(at)
	}

	const onLayout = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)
	// Clamped rather than indexed: a settle can round past the end on a hard flick, and the
	// caption is the one thing on this sheet that must never be blank.
	const step = steps[Math.min(index, steps.length - 1)] ?? steps[0]

	return (
		<VStack gap={0}>
			<View onLayout={onLayout}>
				{width > 0 && (
					<ScrollView
						ref={scroll}
						horizontal
						showsHorizontalScrollIndicator={false}
						snapToInterval={stride}
						decelerationRate="fast"
						disableIntervalMomentum
						onMomentumScrollEnd={onSettled}
						// A short flick can settle without momentum, and a step that scrolled
						// without the dots following it reads as broken dots.
						onScrollEndDrag={onSettled}
						scrollEventThrottle={16}
					>
						{steps.map((item, at) => (
							// Keyed by position, not by photograph: two steps of one walk can share
							// an image — the same corridor photographed once and used for "through
							// the lobby" and "past the kitchen" — and a duplicated key drops one of
							// the pages. The list is static, so the index is a stable identity.
							<View
								key={`${at}-${item.imageUrl}`}
								style={{ marginRight: at === steps.length - 1 ? 0 : GAP }}
							>
								<Step step={item} width={page} />
							</View>
						))}
					</ScrollView>
				)}
			</View>

			{/* The words for the step the carousel has settled on, in a block that does not move.
			
			    Inset seven points, which is where a twenty-point corner puts the photograph's own
			    visual edge: type ranged on the image's mathematical left edge reads as hanging
			    outside it. The right inset is the peek's, so the description never runs under the
			    next photograph. */}
			<VStack
				gap={4}
				style={{ marginTop: 18, paddingLeft: 7, paddingRight: width - page + 7 }}
			>
				<Header variant="h7">{step?.title}</Header>
				<Text size="body1" tone="soft">
					{step?.description}
				</Text>
			</VStack>
		</VStack>
	)
}
